import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderPageData } from '../../../../api';
import { OrderPageStatus } from '../../../../api/models/OrderPageStatus';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getOrderPages } from '../../../../utils/AccountUtils';
import { getActiveLocations } from '../../../../common/filter/locationGrouping';
import { ALL_SECTION_TYPES } from '../../sectionMeta';
import { useAnalyticsFilter } from './AnalyticsFilterContext';

type SummaryLabel = { primary: string; secondary: string };

export const useAnalyticsFilterSummary = (): SummaryLabel => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { isFilterActive, selectedLocationIds, selectedOrderPageIds, sectionTypes } = useAnalyticsFilter();

  return useMemo(() => {
    if (!isFilterActive) return { primary: t('analytics.filter.label'), secondary: '' };

    const parts: string[] = [];
    if (selectedLocationIds !== null) {
      const total = getActiveLocations(account).length;
      parts.push(t('analytics.filter.summary.locations', { count: selectedLocationIds.length, total }));
    }
    if (selectedOrderPageIds !== null) {
      const total = getOrderPages(account).filter((op: OrderPageData) => op.status !== OrderPageStatus.ARCHIVED).length;
      parts.push(t('analytics.filter.summary.workshops', { count: selectedOrderPageIds.length, total }));
    }

    const secondaryParts: string[] = [];
    if (sectionTypes.length !== ALL_SECTION_TYPES.length) {
      secondaryParts.push(
        t('analytics.filter.summary.sections', { count: sectionTypes.length, total: ALL_SECTION_TYPES.length }),
      );
    }

    return {
      primary: parts.join(' · ') || t('analytics.filter.label'),
      secondary: secondaryParts.join(' · '),
    };
  }, [isFilterActive, selectedLocationIds, selectedOrderPageIds, sectionTypes, account, t]);
};
